import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../context/useAuth";
import { authClient } from "../../../utils/authClient";
import { PortalAccess } from "../../../types/user";
import { canAccessQuarantine, canAccessUsers } from "../../../utils/quarantineAccess";
import logoSrc from "../../../assets/OTT-Okta-Tile.png";
import "./NavBar.css";

interface NavItemDef {
  label:           string;
  path:            string;
  adminOnly?:      boolean;
  quarantineOnly?: boolean;
}

interface NavBarProps {
  currentPath:        string;
  onNavigate:         (path: string) => void;
  manualReviewCount?: number;
  portal:             "fyo" | "aro";
  onSwitchPortal:     (portal: "fyo" | "aro") => void;
  portalAccess:       PortalAccess;
}

const fyoSections: { label: string; items: NavItemDef[] }[] = [
  {
    label: "Find Your Ohio",
    items: [
      { label: "AI Analytics",            path: "/find-your-ohio/analytics"       },
      { label: "Candidates",             path: "/find-your-ohio/candidates"      },
      { label: "LinkedIn Tracked FYO Company Hires", path: "/find-your-ohio/linkedin-tracked-fyo-company-hires" },
      // { label: "Direct Placement",       path: "/find-your-ohio/direct_placement" },
      { label: "LinkedIn Tracked Ohio Relocations", path: "/find-your-ohio/linkedin-tracked-ohio-relocations" },
      { label: "LinkedIn Tracked Ohio Hires", path: "/find-your-ohio/linkedin-tracked-ohio-hires" },
      { label: "FYO Companies",           path: "/find-your-ohio/companies" },
    ],
  },
];

const aroSections: { label: string; items: NavItemDef[] }[] = [
  {
    label: "AI Ready Ohio",
    items: [
      { label: "AI Analytics", path: "/ai-ready-ohio/analytics"    },
      { label: "Certificates", path: "/ai-ready-ohio/certificates" },
      { label: "People",       path: "/ai-ready-ohio/people"       },
      { label: "Events",       path: "/ai-ready-ohio/events"       },
      { label: "Import",       path: "/ai-ready-ohio/import"       },
      { label: "AI Report",    path: "/ai-ready-ohio/reports",    quarantineOnly: true },
      { label: "Quarantine",   path: "/ai-ready-ohio/quarantine", quarantineOnly: true },
    ],
  },
];

export default function NavBar({
  currentPath,
  onNavigate,
  manualReviewCount,
  portal,
  onSwitchPortal,
  portalAccess,
}: NavBarProps) {
  const { name, email, role }   = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignOut = async () => {
    try {
      await authClient.signOut({
        revokeAccessToken:      true,
        revokeRefreshToken:     true,
        postLogoutRedirectUri:  window.location.origin,
      });
    } catch {
      await authClient.tokenManager.clear();
      window.location.assign("/");
    }
  };

  const firstName = name ? name.split(" ")[0] : email ?? "User";
  const initials  = name
    ? name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : email?.[0]?.toUpperCase() ?? "?";

  const activeSections = portal === "fyo" ? fyoSections : aroSections;
  const showSwitcher   = portalAccess === "all";
  const helpPath       = portal === "fyo" ? "/find-your-ohio/help" : "/ai-ready-ohio/help";

  return (
    <nav className="sidebar">

      {/* ── Logo ── */}
      <div className="sidebar__logo-wrap">
        <img src={logoSrc} alt="Ohio TalentTrack" className="sidebar__logo" />
      </div>

      {/* ── Portal switcher (all-access users only) ── */}
      {showSwitcher && (
        <div className="sidebar__portal-switcher">
          <span className="sidebar__portal-label">Portal</span>
          <button
            className={`sidebar__portal-btn ${portal === "fyo" ? "sidebar__portal-btn--active" : ""}`}
            onClick={() => onSwitchPortal("fyo")}
          >
            Find Your Ohio
          </button>
          <button
            className={`sidebar__portal-btn ${portal === "aro" ? "sidebar__portal-btn--active" : ""}`}
            onClick={() => onSwitchPortal("aro")}
          >
            AI Ready Ohio
          </button>
        </div>
      )}

      <div className="sidebar__divider" />

      {/* ── Nav links (scrollable) ── */}
      <div className="sidebar__nav">
        {activeSections.map((section) => (
          <div key={section.label} className="sidebar__section">
            <span className="sidebar__section-label">{section.label}</span>
            {section.items
              .filter((item) => !(item.adminOnly && role !== "admin"))
              .filter((item) => !(item.quarantineOnly && !canAccessQuarantine(email)))
              .map((item) => (
                <button
                  key={item.path}
                  className={`sidebar__link ${currentPath.startsWith(item.path) ? "sidebar__link--active" : ""}`}
                  onClick={() => onNavigate(item.path)}
                >
                  {item.label}
                  {item.path === "/find-your-ohio/referrals/review" &&
                    manualReviewCount != null &&
                    manualReviewCount > 0 && (
                      <span className="sidebar__review-badge">{manualReviewCount}</span>
                    )}
                </button>
              ))}
          </div>
        ))}
      </div>

      {/* ── User (pinned to bottom) ── */}
      <div className="sidebar__user-section">
        {portal === "fyo" && (
          <button
            className={`sidebar__help-btn ${currentPath.startsWith("/find-your-ohio/feedback") ? "sidebar__help-btn--active" : ""}`}
            onClick={() => onNavigate("/find-your-ohio/feedback")}
          >
            <i className="fas fa-comment-dots" />
            Feedback
          </button>
        )}
        <button
          className={`sidebar__help-btn ${currentPath.startsWith(helpPath) ? "sidebar__help-btn--active" : ""}`}
          onClick={() => onNavigate(helpPath)}
        >
          <i className="fas fa-circle-question" />
          Help Center
        </button>
        <div className="sidebar__divider" />
        <div className="sidebar__user" ref={menuRef}>
          <button className="sidebar__avatar-btn" onClick={() => setMenuOpen((o) => !o)}>
            <span className="sidebar__avatar">{initials}</span>
            <div className="sidebar__user-info">
              <span className="sidebar__user-name">{firstName}</span>
              <span className="sidebar__user-role">{role}</span>
            </div>
            <span className="sidebar__chevron">{menuOpen ? "▲" : "▼"}</span>
          </button>

          {menuOpen && (
            <div className="sidebar__dropdown">
              <div className="sidebar__dropdown-header">
                <span className="sidebar__dropdown-name">{name || "User"}</span>
                <span className="sidebar__dropdown-email">{email}</span>
                <span className="sidebar__dropdown-role">{role}</span>
              </div>
              <div className="sidebar__dropdown-divider" />
              {canAccessUsers(email) && (
                <button
                  className="sidebar__dropdown-item"
                  onClick={() => { setMenuOpen(false); onNavigate("/users"); }}
                >
                  Users
                </button>
              )}
              <button className="sidebar__dropdown-item sidebar__dropdown-item--disabled">
                Settings
              </button>
              <button className="sidebar__dropdown-item" onClick={handleSignOut}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

    </nav>
  );
}
